import { BACKEND_URL } from '../config';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Leaf, ArrowLeft, User, Mail, Phone, Calendar, CreditCard, Shield } from 'lucide-react';

const AccountInfo = ({ user, token, language, onLogout, onToggleLanguage }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/stats`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const translations = {
    en: {
      title: 'Account Information',
      back: 'Back to Dashboard',
      personalInfo: 'Personal Information',
      email: 'Email',
      mobile: 'Mobile Number',
      language: 'Preferred Language',
      subscriptionInfo: 'Subscription Information',
      currentPlan: 'Current Plan',
      status: 'Status',
      memberSince: 'Member Since',
      subscriptionEnd: 'Subscription Ends',
      daysRemaining: 'Days Remaining',
      usageStats: 'Usage Statistics',
      totalScans: 'Total Scans',
      scansToday: 'Scans Today',
      remaining: 'Remaining Today',
      upgrade: 'Upgrade to Premium',
      renew: 'Renew Subscription'
    },
    hi: {
      title: 'खाता जानकारी',
      back: 'डैशबोर्ड पर वापस जाएं',
      personalInfo: 'व्यक्तिगत जानकारी',
      email: 'ईमेल',
      mobile: 'मोबाइल नंबर',
      language: 'पसंदीदा भाषा',
      subscriptionInfo: 'सदस्यता जानकारी',
      currentPlan: 'वर्तमान योजना',
      status: 'स्थिति',
      memberSince: 'सदस्य बनने की तारीख',
      subscriptionEnd: 'सदस्यता समाप्ति',
      daysRemaining: 'शेष दिन',
      usageStats: 'उपयोग आंकड़े',
      totalScans: 'कुल स्कैन',
      scansToday: 'आज के स्कैन',
      remaining: 'आज शेष',
      upgrade: 'प्रीमियम में अपग्रेड करें',
      renew: 'सदस्यता नवीनीकरण करें'
    }
  };

  const t = translations[language];

  return (
    <div className="account-page">
      <header className="app-header">
        <div className="header-content">
          <div className="logo">
            <Leaf size={32} />
            <span>BoAI</span>
          </div>
          <div className="header-actions">
            <button className="lang-toggle" onClick={onToggleLanguage}>
              {language.toUpperCase()}
            </button>
            <button className="icon-btn" onClick={onLogout}>
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="account-content">
        <div className="container">
          <div className="page-header">
            <button className="btn-secondary" onClick={() => navigate('/dashboard')}>
              <ArrowLeft size={18} /> {t.back}
            </button>
            <h2>{t.title}</h2>
          </div>

          <div className="account-grid">
            {/* Personal Information */}
            <div className="account-card">
              <div className="card-header">
                <User size={24} />
                <h3>{t.personalInfo}</h3>
              </div>
              <div className="info-list">
                <div className="info-item">
                  <Mail size={18} />
                  <div>
                    <p className="info-label">{t.email}</p>
                    <p className="info-value">{user?.email}</p>
                  </div>
                </div>
                <div className="info-item">
                  <Phone size={18} />
                  <div>
                    <p className="info-label">{t.mobile}</p>
                    <p className="info-value">{user?.mobile}</p>
                  </div>
                </div>
                <div className="info-item">
                  <Shield size={18} />
                  <div>
                    <p className="info-label">{t.language}</p>
                    <p className="info-value">{user?.language === 'hi' ? 'हिंदी (Hindi)' : 'English'}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Subscription Information */}
            <div className="account-card">
              <div className="card-header">
                <CreditCard size={24} />
                <h3>{t.subscriptionInfo}</h3>
              </div>
              <div className="info-list">
                <div className="info-item">
                  <div className={`plan-badge ${user?.tier}`}>
                    {user?.tier?.toUpperCase() || 'FREE'}
                  </div>
                  <div>
                    <p className="info-label">{t.currentPlan}</p>
                    <p className="info-value">
                      {user?.tier === 'paid' ? 'Premium - ₹499/month' : 'Free - ₹0/month'}
                    </p>
                  </div>
                </div>
                <div className="info-item">
                  <Calendar size={18} />
                  <div>
                    <p className="info-label">{t.memberSince}</p>
                    <p className="info-value">{formatDate(user?.created_at)}</p>
                  </div>
                </div>
                {user?.tier === 'paid' && (
                  <>
                    <div className="info-item">
                      <Calendar size={18} />
                      <div>
                        <p className="info-label">{t.subscriptionEnd}</p>
                        <p className="info-value">{formatDate(user?.subscription_end)}</p>
                      </div>
                    </div>
                    {stats?.days_remaining !== null && (
                      <div className="info-item">
                        <div className={`days-badge ${stats.days_remaining <= 7 ? 'warning' : ''}`}>
                          {stats.days_remaining}
                        </div>
                        <div>
                          <p className="info-label">{t.daysRemaining}</p>
                          <p className="info-value">
                            {stats.days_remaining <= 7 ? 'Renew soon!' : 'Active subscription'}
                          </p>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              
              {user?.tier === 'free' ? (
                <button className="btn-primary" onClick={() => navigate('/payment')}>
                  {t.upgrade}
                </button>
              ) : stats?.days_remaining <= 7 && (
                <button className="btn-primary" onClick={() => navigate('/payment')}>
                  {t.renew}
                </button>
              )}
            </div>

            {/* Usage Stats */}
            <div className="account-card full-width">
              <div className="card-header">
                <Shield size={24} />
                <h3>{t.usageStats}</h3>
              </div>
              {stats && (
                <div className="stats-grid-mini">
                  <div className="stat-mini">
                    <p className="stat-value">{stats.total_scans}</p>
                    <p className="stat-label">{t.totalScans}</p>
                  </div>
                  <div className="stat-mini">
                    <p className="stat-value">{stats.scans_today}</p>
                    <p className="stat-label">{t.scansToday}</p>
                  </div>
                  <div className="stat-mini">
                    <p className="stat-value">
                      {stats.scans_remaining === 'unlimited' ? '∞' : stats.scans_remaining}
                    </p>
                    <p className="stat-label">{t.remaining}</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountInfo;
